import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  IconButton,
  InputAdornment,
  useTheme,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Logo from '../landing/Logo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, rememberMe, setRememberMe } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  
  // Track if we've already initiated a redirect to prevent loops
  const hasRedirectedRef = useRef(false);

  // Check for session timeout message (only on mount)
  useEffect(() => {
    const timeout = new URLSearchParams(location.search).get('timeout');
    if (timeout === 'true') {
      setError('Your session has expired. Please log in again.');
      // Clear the timeout param from URL to prevent showing message on refresh
      window.history.replaceState({}, '', '/login');
    }
  }, []); // Empty dependency - only run on mount

  // Redirect is now handled by AuthRoute wrapper - no need for duplicate logic here

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading || hasRedirectedRef.current) return;
    
    try {
      setError('');
      setLoading(true);
      await login(email, password, rememberMe);
      
      // Mark as redirected and navigate
      hasRedirectedRef.current = true;
      navigate('/', { replace: true });
    } catch (error) {
      setError('Failed to sign in: ' + error.message);
      setLoading(false);
      hasRedirectedRef.current = false;
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0fdf4',
        backgroundImage: 'linear-gradient(45deg, #f0fdf4 0%, #ffffff 100%)',
        pt: { xs: 1, sm: 2 }
      }}
    >
      <Container maxWidth="xs">
        <Card
          elevation={2}
          sx={{
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Link to="/">
                <Logo height={50} />
              </Link>
            </Box>

            <Typography
              variant="h4"
              component="h1"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 3,
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }}
            >
              Welcome Back
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: theme.palette.primary.main,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label="Remember me"
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  py: 1.5,
                  backgroundColor: '#96ffa8',
                  color: '#1B5E20',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#82e694',
                  },
                }}
              >
                Sign In
              </Button>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Link
                  to="/forgot-password"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>
            </form>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Sign up
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login; 